// Single source of truth for the app's route table.
//
// Both the client app (src/App.tsx, wrapped in BrowserRouter) and the SSG
// prerender (src/entry-server.tsx, wrapped in StaticRouter) render the SAME
// <Routes> built from `appRoutes` below, so the client and the prerendered HTML
// always agree on what each path renders.
//
// The prerender script (scripts/prerender.mjs) and the sitemap generator
// (scripts/generate-sitemap.mjs) consume `getStaticRoutePaths()` so the set of
// statically generated pages is derived from this one list and cannot drift from
// the route table.
//
// Four kinds of entry:
//   - page:     a real page rendered at a fixed path. Prerendered to static HTML.
//   - dynamic:  a path with a param (e.g. /post/:slug). NOT enumerated here — the
//               article slugs are prerendered separately by prerender.mjs.
//   - redirect: a pure client-side <Navigate>. NOT prerendered (no real content);
//               it resolves at runtime via the SPA fallback, or via a real
//               Vercel redirect in vercel.json for high-value share URLs.
//   - document: standalone HTML copied from public/. Client links load the full
//               document; SSR must not overwrite it with the React shell.

import type { ComponentType } from 'react';
import { PROGRAM_DOCUMENTS, PROGRAM_REDIRECTS } from './utils/ai30ProgramRouting.mjs';

import Index from './pages/Index';
import OpenAIPartner from './pages/OpenAIPartner';
import Call from './pages/Call';
import AICall from './pages/AICall';
import Calls from './pages/Calls';
import CallConfirmed from './pages/CallConfirmed';
import Launch from './pages/Launch';
import December from './pages/December';
import January from './pages/January';
import Scale from './pages/Scale';
import Marketing from './pages/Marketing';
import CFO from './pages/CFO';
import Accredited from './pages/Accredited';
import Articles from './pages/Articles';
import Article from './pages/Article';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Refer from './pages/Refer';
import AlternativeToSidebar from './pages/AlternativeToSidebar';
import AlternativeToMBA from './pages/AlternativeToMBA';
import AlternativeToMitOxford from './pages/AlternativeToMitOxford';
import RGAWorkshop from './pages/RGAWorkshop';
import PlaybookConfirmed from './pages/PlaybookConfirmed';
import Trends from './pages/Trends';
import AiExecutiveStrategy from './pages/AiExecutiveStrategy';
import AiPlaybookExecutives from './pages/AiPlaybookExecutives';
import CallTemplates from './pages/CallTemplates';
import LaunchWithUs from './pages/LaunchWithUs';
import HowToModelYourOffer from './pages/HowToModelYourOffer';
import FinanceForFounders from './pages/FinanceForFounders';
import FinanceForFoundersWorkshop from './pages/FinanceForFoundersWorkshop';
import FinanceWorkshopConfirmed from './pages/FinanceWorkshopConfirmed';
import BrandCharacterWorkshop from './pages/BrandCharacterWorkshop';
import BrandWorkshopConfirmed from './pages/BrandWorkshopConfirmed';
import VideoWorkshop from './pages/VideoWorkshop';
import HowToWinVisibilityWorkshop from './pages/HowToWinVisibilityWorkshop';
import VisibilityWorkshopConfirmed from './pages/VisibilityWorkshopConfirmed';
import AIForLeadersSummary from './pages/AIForLeadersSummary';
import Benchmark from './pages/Benchmark';
import AIAgentVelocityCheckin from './pages/AIAgentVelocityCheckin';
import TenBeforeTen from './pages/TenBeforeTen';
import GetPlaybook from './pages/GetPlaybook';
import AIBuilderPlaybook from './pages/AIBuilderPlaybook';
import AiRdSystem from './pages/AiRdSystem';
import AIAgentStarter from './pages/AIAgentStarter';
import LonghandAIOverview from './pages/LonghandAIOverview';
import LonghandAIProgram from './pages/LonghandAIProgram';
import LonghandAIPrep from './pages/LonghandAIPrep';
import LegitScriptLonghandOnboarding from './pages/LegitScriptLonghandOnboarding';
import HowItWorks from './pages/HowItWorks';
import Speak from './pages/Speak';
import AIProgramSelector from './pages/AIProgramSelector';
import Fortune100PromptLibrary from './pages/Fortune100PromptLibrary';
import PromptLibraryWorkflow from './pages/PromptLibraryWorkflow';
import { library as promptWorkflows } from './data/aiWorkflows';
import { workflowPath } from './utils/promptLibraryShare';
import Library from './pages/Library';
import LibraryConfirmed from './pages/LibraryConfirmed';
import Playbook from './pages/Playbook';
import AIWorkflows from './pages/AIWorkflows';
import WorkshopReplay from './pages/WorkshopReplay';
import AIWorkshop from './pages/AIWorkshop';
import AIWorkshopConfirmed from './pages/AIWorkshopConfirmed';
import AI30 from './pages/AI30';
import AISOS from './pages/AISOS';
import ClaudeMasterclass from './pages/ClaudeMasterclass';
import CopilotCowork from './pages/CopilotCowork';
import Home2025 from './pages/Home2025';
import OfficeHoursDemo from './pages/OfficeHoursDemo';
import Podcast from './pages/Podcast';
import HealthcareAICaseStudy from './pages/HealthcareAICaseStudy';
import Assessment from './pages/Assessment';
import AIPriorities from './pages/AIPriorities';
import TDARResultsFormula from './pages/TDARResultsFormula';
import DecisionMakingProcess from './pages/DecisionMakingProcess';
import ResponsibleAIPractice from './pages/ResponsibleAIPractice';
import AIWaitlist from './pages/AIWaitlist';
import RGAPlaybook from './pages/RGAPlaybook';
import AICoworker from './pages/AICoworker';
import AISOSLanding from './pages/AISOSLanding';
import ClaudeWorkshopReplay from './pages/ClaudeWorkshopReplay';
import LKOfficeHours from './pages/LKOfficeHours';
import OfficeHours from './pages/OfficeHours';
import TDownload from './pages/TDownload';
import FirstTimeFounder from './pages/FirstTimeFounder';
import ClaudeBasics from './pages/ClaudeBasics';
import S2PElevateAI from './pages/S2PElevateAI';
import Elevate from './pages/Elevate';
import S2PElevateAIRockline from './pages/S2PElevateAIRockline';
import S2PAIBuilderPlaybook from './pages/S2PAIBuilderPlaybook';
import S2PAIBusinessCouncilAugust from './pages/S2PAIBusinessCouncilAugust';
import S2PAIBusinessCouncilNovember from './pages/S2PAIBusinessCouncilNovember';
import ForwardDeployedEducators from './pages/ForwardDeployedEducators';
import TheClub from './pages/TheClub';
import NotFound from './pages/NotFound';

// A real page rendered at a fixed path. These get prerendered to static HTML.
export interface PageRoute {
  kind: 'page';
  path: string;
  Component: ComponentType;
  indexable: boolean;
}

// A path that renders a component but takes a route param. Enumerated/prerendered
// elsewhere (article slugs), so it is NOT included in getStaticRoutePaths().
export interface DynamicRoute {
  kind: 'dynamic';
  path: string;
  Component: ComponentType;
}

// A pure client-side redirect (<Navigate to=... replace />). Not prerendered.
export interface RedirectRoute {
  kind: 'redirect';
  path: string;
  to: string;
  preserveLocation?: boolean;
}

// Standalone HTML is copied from public/. Client navigation must load the
// document, while SSR leaves the file intact instead of prerendering over it.
export interface DocumentRoute {
  kind: 'document';
  path: string;
}

export type AppRoute = PageRoute | DynamicRoute | RedirectRoute | DocumentRoute;

const page = (
  path: string,
  Component: ComponentType,
  options: { indexable?: boolean } = {},
): PageRoute => ({
  kind: 'page',
  path,
  Component,
  indexable: options.indexable ?? true,
});
const dynamic = (path: string, Component: ComponentType): DynamicRoute => ({
  kind: 'dynamic',
  path,
  Component,
});
const redirect = (path: string, to: string, preserveLocation = false): RedirectRoute => ({
  kind: 'redirect',
  path,
  to,
  preserveLocation,
});

// The route table, in render order. Add new pages here — they are automatically
// rendered by the client, prerendered to static HTML, and added to the sitemap.
export const appRoutes: AppRoute[] = [
  page('/', Index),
  page('/openai-partner', OpenAIPartner),
  page('/pt-br/openai-partner', OpenAIPartner),
  page('/ai-agent-velocity-check-in', AIAgentVelocityCheckin),
  page('/call', Call),
  page('/ai-call', AICall),
  page('/pt-br/ai-call', AICall),
  page('/calls', Calls),
  page('/call-confirmed', CallConfirmed),
  redirect('/ai-trade-school', '/ai-30', true),
  page('/launch-old', Launch),
  redirect('/launch', '/january'),
  page('/december', December),
  page('/january', January),
  page('/scale', Scale),
  page('/marketing', Marketing),
  page('/cfo', CFO),
  page('/accredited', Accredited),
  page('/articles', Articles),
  dynamic('/post/:slug', Article),
  page('/privacy-policy', PrivacyPolicy),
  page('/refer', Refer),
  page('/alternative-to-sidebar', AlternativeToSidebar),
  page('/alternative-to-mba', AlternativeToMBA),
  page('/alternative-to-mit-oxford', AlternativeToMitOxford),
  page('/in-demand', RGAWorkshop),
  page('/playbook-confirmed', PlaybookConfirmed),
  // Retire only the paid membership sales page; workshop/member subpaths stay live.
  redirect('/ai-mastermind', '/ai-30', true),
  page('/trends', Trends),
  redirect('/ai-office-hours', '/ai-30', true),
  redirect('/ship-ai', '/ai-30', true),
  redirect('/ship-ai-training-program', '/ai-30', true),
  page('/ai-executive-strategy-program', AiExecutiveStrategy),
  page('/ai-playbook-executives', AiPlaybookExecutives),
  page('/call-templates', CallTemplates),
  page('/launch-with-us', LaunchWithUs),
  page('/how-to-model-your-offer', HowToModelYourOffer),
  page('/finance-for-founders', FinanceForFounders),
  page('/finance-for-founders-workshop', FinanceForFoundersWorkshop),
  page('/finance-workshop-confirmed', FinanceWorkshopConfirmed),
  page('/brand-character-workshop', BrandCharacterWorkshop),
  page('/brand-workshop-confirmed', BrandWorkshopConfirmed),
  page('/video-workshop', VideoWorkshop),
  page('/how-to-win-visibility-workshop', HowToWinVisibilityWorkshop),
  page('/visibility-workshop-confirmed', VisibilityWorkshopConfirmed),
  page('/ai', AIProgramSelector),
  redirect('/ai-for-vc', '/ai-30', true),
  redirect('/ai-for-leaders', '/ai-30', true),
  redirect('/ai-for-leaders/summary', '/ai-30/summary', true),
  page('/forward-deployed-educators', ForwardDeployedEducators),
  page('/the-club', TheClub, { indexable: false }),
  page('/benchmark', Benchmark),
  page('/10-before-10', TenBeforeTen),
  page('/full-playbook', GetPlaybook),
  page('/ai-builder-playbook', AIBuilderPlaybook),
  redirect('/claude-agents-playbook', '/ai-builder-playbook'),
  // Renamed from /claude-code-playbook. The old path is live and linked from the
  // sitemap and other pages, so it keeps resolving — vercel.json serves the 301 for
  // real traffic, and this entry covers in-app navigation to the old path.
  redirect('/claude-code-playbook', '/ai-builder-playbook'),
  page('/ai-workflows-old', AIWorkflows),
  redirect('/ai-workflows', '/ai-workshop', true),
  page('/ai-workshop', AIWorkshop),
  page('/ai-workshop/confirmed', AIWorkshopConfirmed, { indexable: false }),
  redirect('/workshop', '/ai-workshop', true),
  redirect('/ai-agent-skills', '/ai-workshop', true),
  redirect('/join', '/ai-workshop', true),
  page('/workshop-replay', WorkshopReplay),
  redirect('/ai-workspace', '/ai-builder-playbook'),
  redirect('/ai-workflow', '/ai-builder-playbook'),
  redirect('/ai-agent-basics', '/ai-builder-playbook'),
  redirect('/advanced-claude', '/ai-builder-playbook'),
  redirect('/ai-for-suits', '/ai-30', true),
  page('/ai-rd-system', AiRdSystem),
  page('/ai-agent-starter', AIAgentStarter),
  page('/ai-30/program/overview', LonghandAIOverview),
  page('/ai-30/program/atlas', LonghandAIProgram),
  page('/ai-30/program/prep', LonghandAIPrep),
  ...Object.keys(PROGRAM_DOCUMENTS).map((path): DocumentRoute => ({ kind: 'document', path })),
  ...Object.entries(PROGRAM_REDIRECTS).map(([from, to]) => redirect(from, to, !to.includes('#'))),
  redirect('/longhand-onboarding', '/legitscript-longhand-onboarding'),
  page('/legitscript-longhand-onboarding', LegitScriptLonghandOnboarding),
  page('/how-it-works', HowItWorks),
  page('/speak', Speak),
  // Delivered by email or a saved link. Keep these complete and accessible, but
  // exclude the catalog and every workflow from public discovery surfaces.
  page('/fortune-100-prompt-library', Fortune100PromptLibrary, { indexable: false }),
  ...promptWorkflows.map((workflow) => page(workflowPath(workflow.slug), PromptLibraryWorkflow, { indexable: false })),
  redirect('/fortune-100-ai-workspace', '/ai-builder-playbook'),
  page('/library', Library),
  page('/library-confirmed', LibraryConfirmed, { indexable: false }),
  page('/playbook', Playbook),
  redirect('/ai-program', '/ai-30', true),
  page('/ai-30', AI30),
  page('/ai-30/summary', AIForLeadersSummary, { indexable: false }),
  redirect('/ai-first-program', '/ai-30', true),
  page('/ai-sos', AISOS),
  page('/claude-masterclass', ClaudeMasterclass),
  page('/copilot-cowork', CopilotCowork),
  redirect('/ai-sos-workspace', '/claude-masterclass'),
  page('/ai-mastermind/claude-code', OfficeHoursDemo),
  { kind: 'document', path: '/ai30-slides' },
  { kind: 'document', path: '/ai-mastermind/10-28-26' },
  redirect('/office-hours-demo', '/ai-mastermind/claude-code'),
  page('/podcast', Podcast),
  page('/healthcare-ai-case-study', HealthcareAICaseStudy),
  page('/2025', Home2025),
  redirect('/2026', '/'),
  page('/assessment', Assessment),
  page('/ai-priorities', AIPriorities),
  page('/tdar-results-formula', TDARResultsFormula),
  page('/decision-making-process', DecisionMakingProcess),
  page('/responsible-ai-practice', ResponsibleAIPractice),
  page('/ai-waitlist', AIWaitlist),
  // The Fortune 100 library is the canonical home for the shared 40-workflow
  // catalog. Preserve saved and indexed legacy URLs without a redirect chain.
  redirect('/top-40-ai', '/fortune-100-prompt-library'),
  redirect('/40-ai-tricks', '/fortune-100-prompt-library'),
  redirect('/26-workflows', '/fortune-100-prompt-library'),
  page('/rga-playbook', RGAPlaybook),
  page('/ai-coworker', AICoworker),
  page('/longhand', AISOSLanding),
  redirect('/t', '/longhand'),
  page('/t-download', TDownload),
  redirect('/launch-guide', '/rga-playbook'),
  redirect('/claude-workshop', '/ai-30', true),
  page('/claude-workshop-replay', ClaudeWorkshopReplay),
  redirect('/ai-agent-system', '/ai-30', true),
  page('/ai-mastermind/consultants', LKOfficeHours),
  redirect('/lk-office-hours', '/ai-mastermind/consultants'),
  page('/ai-mastermind/four-gears', OfficeHours),
  redirect('/office-hours', '/ai-mastermind/four-gears'),
  page('/first-time-founder', FirstTimeFounder),
  redirect('/claude-meta-prompting', '/ai-builder-playbook#shape-before-building'),
  page('/claude-fundamentals', ClaudeBasics),
  redirect('/claude-basics', '/claude-fundamentals'),
  page('/s2p-elevate-ai', S2PElevateAI),
  page('/elevate-mindset', Elevate),
  redirect('/elevate', '/elevate-mindset', true),
  page('/s2p-elevate-ai-builder', S2PAIBuilderPlaybook),
  redirect('/s2p-elevate-ai-vibe-code', '/s2p-elevate-ai-builder'),
  page('/s2p-elevate-ai-rockline', S2PElevateAIRockline, { indexable: false }),
  redirect('/s2p-elevate-ai-rockline-vibe-code', '/s2p-elevate-ai-builder'),
  page('/s2p-ai-business-council-august', S2PAIBusinessCouncilAugust),
  page('/s2p-ai-business-council-november', S2PAIBusinessCouncilNovember),
  redirect('/trainings', '/ai-30/program', true),
];

// The catch-all 404, rendered last (after every route above). Kept separate so it
// is never treated as a prerenderable page.
export const NotFoundRoute = NotFound;

// Every real, fixed-path page route, as plain path strings. This is what the
// prerender and sitemap consume. Excludes:
//   - redirect routes (no real content; handled client-side / via vercel.json)
//   - document routes (standalone HTML copied from public/)
//   - dynamic routes like /post/:slug (article slugs are prerendered separately)
//   - the "*" catch-all (NotFound)
export const getStaticRoutePaths = (): string[] =>
  appRoutes.filter((r): r is PageRoute => r.kind === 'page').map((r) => r.path);

// Company-specific and other unlisted pages still prerender for a fast, complete
// experience, but they stay out of the public sitemap when indexable is false.
export const getSitemapRoutePaths = (): string[] =>
  appRoutes
    .filter((r): r is PageRoute => r.kind === 'page' && r.indexable)
    .map((r) => r.path);

// Every client-side <Navigate> redirect, as { path, to } pairs. vercel.json must
// mirror these as real 308s: without the blanket SPA rewrite there is no HTML to
// load at these paths, so nothing would run the <Navigate>. Kept derived rather
// than re-listed so the two cannot drift — scripts/check-vercel-routes.mjs fails
// the build when vercel.json is missing one.
export const getRedirectRoutes = (): { path: string; to: string }[] =>
  appRoutes
    .filter((r): r is RedirectRoute => r.kind === 'redirect')
    .map((r) => ({ path: r.path, to: r.to }));

// Route patterns that take a param (today: /post/:slug). Slugs present in the
// committed snapshot are prerendered individually, but a slug published after the
// last snapshot has no static file and must still resolve, so vercel.json keeps a
// targeted SPA rewrite for these prefixes.
export const getDynamicRoutePaths = (): string[] =>
  appRoutes.filter((r): r is DynamicRoute => r.kind === 'dynamic').map((r) => r.path);
